import { create } from 'zustand';
import useToastStore from './toastStore';
import { markBoardUnlocked } from '../utils/boardUnlock';

const getToken = () => localStorage.getItem('token');

const authFetch = async (url, options = {}) => {
  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
      ...options.headers,
    },
  });
  let data = {};
  try { data = await res.json(); } catch {}
  if (!res.ok || !data.success) {
    const err = new Error(data.error || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return data.data;
};

const RECENT_KEY = (uid) => `seasymon:recent:${uid}`;
const RECENT_LIMIT = 3;

// Diretório de pessoas sai do tique de polling (T11/§7.4): só é pedido ao
// abrir o quadro e renovado a cada USERS_REFRESH_MS.
const USERS_REFRESH_MS = 10 * 60 * 1000;

// =============== Helpers de diff para polling ===============
// Comparação rasa de objetos planos (1 nivel) — suficiente pra cards/columns/groups/users
// que são objetos planos vindos do backend.
function shallowEqual(a, b) {
  if (a === b) return true;
  if (!a || !b || typeof a !== 'object' || typeof b !== 'object') return false;
  const ak = Object.keys(a);
  const bk = Object.keys(b);
  if (ak.length !== bk.length) return false;
  for (const k of ak) {
    if (a[k] !== b[k]) return false;
  }
  return true;
}

// Compara my_access (GET /boards/:id/data) por valor, nao por referencia —
// o backend manda um objeto `can` novo a cada resposta, entao `===`/shallowEqual
// de 1 nivel sempre bateria "diferente" e geraria um patch a cada poll (5s).
function sameMyAccess(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.level !== b.level || a.source !== b.source) return false;
  const ac = a.can || {};
  const bc = b.can || {};
  const keys = new Set([...Object.keys(ac), ...Object.keys(bc)]);
  for (const k of keys) { if (ac[k] !== bc[k]) return false; }
  return true;
}

// Para listas de objetos com id: preserva referencia de itens nao modificados
// e retorna o array antigo se nada mudou. Fallback seguro: se shapes divergem
// muito (tamanho zero -> N, ou IDs faltando), retorna a lista nova como esta.
function reconcileListById(oldList, newList) {
  if (!Array.isArray(oldList) || !Array.isArray(newList)) return newList;
  if (oldList === newList) return oldList;
  const oldById = new Map(oldList.map((x) => [x.id, x]));
  let changed = oldList.length !== newList.length;
  const merged = newList.map((item) => {
    const prev = oldById.get(item.id);
    if (!prev) { changed = true; return item; }
    if (shallowEqual(prev, item)) return prev;
    changed = true;
    return item;
  });
  return changed ? merged : oldList;
}

// Para cardValues (mapa "cardId:columnId" -> value JSON):
// preserva referencia se nada mudou. Compara por igualdade rasa de chaves
// e por igualdade estrita de valor (JSON values do backend são reconstruidos
// a cada response, entao comparamos via JSON.stringify quando difere por ===).
function reconcileCardValues(oldMap, newMap) {
  if (oldMap === newMap) return oldMap;
  const ok = Object.keys(oldMap);
  const nk = Object.keys(newMap);
  if (ok.length !== nk.length) return newMap;
  for (const k of nk) {
    if (!(k in oldMap)) return newMap;
    const a = oldMap[k];
    const b = newMap[k];
    if (a === b) continue;
    // valores podem ser null, primitivos ou objetos pequenos (status, link, etc)
    if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') return newMap;
    try {
      if (JSON.stringify(a) !== JSON.stringify(b)) return newMap;
    } catch {
      return newMap;
    }
  }
  return oldMap;
}

// Para statusOptions (mapa columnId -> array de options):
// reconcilia cada array; preserva referencia geral se nenhum mudou.
function reconcileStatusOptions(oldMap, newMap) {
  if (oldMap === newMap) return oldMap;
  const ok = Object.keys(oldMap);
  const nk = Object.keys(newMap);
  if (ok.length !== nk.length) return newMap;
  let anyChanged = false;
  const out = {};
  for (const k of nk) {
    if (!(k in oldMap)) return newMap;
    const merged = reconcileListById(oldMap[k], newMap[k]);
    out[k] = merged;
    if (merged !== oldMap[k]) anyChanged = true;
  }
  return anyChanged ? out : oldMap;
}

function loadRecentsFromStorage(userId) {
  if (!userId) return [];
  try {
    const raw = localStorage.getItem(RECENT_KEY(userId));
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.slice(0, RECENT_LIMIT) : [];
  } catch { return []; }
}

function saveRecentsToStorage(userId, ids) {
  if (!userId) return;
  try { localStorage.setItem(RECENT_KEY(userId), JSON.stringify(ids.slice(0, RECENT_LIMIT))); } catch {}
}

// Epoch counter pra detectar mutacoes de cardValues durante um poll em voo.
// Module-level (fora do state reativo) pra evitar loops de re-render (React #185).
let _cardValuesEpoch = 0;
const bumpCardValuesEpoch = () => { _cardValuesEpoch++; };
const getCardValuesEpoch = () => _cardValuesEpoch;

// Mesma estrategia pra cards (move/create/update/delete/archive). Evita que
// um poll em voo sobrescreva mutacao otimista local — bug classico de "card
// volta pro lugar original apos drag".
let _cardsEpoch = 0;
const bumpCardsEpoch = () => { _cardsEpoch++; };
const getCardsEpoch = () => _cardsEpoch;

// =============== Mutex de mutacao de cards ===============
// Contador re-entrante (suporta mutacoes concorrentes) + grace period.
// Enquanto > 0 OU dentro do grace period, polling/fetchBoardData NAO sobrescrevem
// o array de cards. Cobre:
//   - PUT em voo (qualquer mutacao otimista)
//   - Janela curta apos PUT confirmar onde o backend pode retornar dado pre-mutacao
//     em consultas concorrentes (replica lag, cache, etc.)
//   - Polling concorrente que iniciou ANTES da mutacao mas resolve DEPOIS do finally
const CARDS_MUTATION_GRACE_MS = 3000;
let _cardsMutationCount = 0;
let _cardsMutationGraceUntil = 0;
const beginCardsMutation = () => { _cardsMutationCount++; };
const endCardsMutation = () => {
  if (_cardsMutationCount > 0) _cardsMutationCount--;
  _cardsMutationGraceUntil = Date.now() + CARDS_MUTATION_GRACE_MS;
};
const isCardsMutating = () => {
  if (_cardsMutationCount > 0) return true;
  return Date.now() < _cardsMutationGraceUntil;
};

// Logs temporarios condicionais. Ativar via `window.__DEBUG_DRAG__ = true` no console.
const dragLog = (...args) => {
  if (typeof window !== 'undefined' && window.__DEBUG_DRAG__) {
    // eslint-disable-next-line no-console
    console.debug('[drag]', ...args);
  }
};

const useBoardStore = create((set, get) => ({
  workspaces: [],
  boards: [],
  currentBoard: null,
  // my_access do GET /boards/:id/data (contrato pos-M1a): { level, source, can }.
  // null quando o backend ainda nao manda o campo (M1a) — useBoardCan cai no
  // fallback antigo nesse caso.
  myAccess: null,
  groups: [],
  columns: [],
  cards: [],
  cardValues: {},
  statusOptions: {},
  users: [],
  usersLoadedAt: 0,
  favoriteFolders: [],
  workspaceFolders: [],
  selectedBoardIds: [],
  recentBoardIds: [],
  sidebarDropHint: null, // { boardId | folderId, position: 'before'|'after' } durante drag
  sidebarDragKind: null, // 'board' | 'folder' — tipo do item arrastado na sidebar
  recentUserId: null,
  loadingWorkspaces: false,
  loadingBoards: false,
  loadingBoard: false,
  loadingFavorites: false,
  editMode: false,
  _pollTimer: null,
  _pollInFlight: false,
  _sidebarPollTimer: null,
  boardMembers: [],
  boardError: null,
  // ================= ACESSO AO QUADRO (M1 — niveis viewer/contributor/editor/owner/deny) =================
  // Estado paralelo ao boardMembers antigo (role/allowed_groups) — mantido intacto porque
  // GroupSection.jsx, BoardTable.jsx e BoardToolbar.jsx ainda leem boardMembers[].role pra
  // decidir quem pode criar card. A integracao dos dois sistemas fica pra outra sessao.
  boardAccess: [],
  loadingBoardAccess: false,
  boardAccessError: null,
  roles: [],
  loadingRoles: false,
  selectedCardIds: [],
  pendingCardValueEdits: new Set(),
  pendingCardMoves: new Set(),
  isDragging: false,

  setEditMode: (v) => set({ editMode: !!v }),
  toggleEditMode: () => set((s) => ({ editMode: !s.editMode })),
  setDragging: (v) => set({ isDragging: !!v }),

  fetchWorkspaces: async () => {
    set({ loadingWorkspaces: true });
    try {
      const workspaces = await authFetch('/api/v1/workspaces');
      set({ workspaces });
    } finally {
      set({ loadingWorkspaces: false });
    }
  },

  // extra: { members?: [{user_id, level}], role_ids?: number[] } — usados pelo
  // dialogo completo de criacao (WorkspaceCreateModal). Omitidos, o POST fica
  // igual ao fluxo simples de sempre (so nome, criado por qualquer usuario).
  createWorkspace: async (name, icon = null, extra = {}) => {
    const body = { name, icon };
    if (extra.members) body.members = extra.members;
    if (extra.role_ids) body.role_ids = extra.role_ids;
    const ws = await authFetch('/api/v1/workspaces', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    set((s) => ({ workspaces: [...s.workspaces, ws] }));
    return ws;
  },

  updateWorkspace: async (id, fields) => {
    const ws = await authFetch(`/api/v1/workspaces/${id}`, {
      method: 'PUT',
      body: JSON.stringify(fields),
    });
    set((s) => ({
      workspaces: s.workspaces.map((w) => (w.id === id ? { ...w, ...ws } : w)),
    }));
    return ws;
  },

  uploadWorkspaceIcon: async (id, file) => {
    const form = new FormData();
    form.append('icon', file);
    const res = await fetch(`/api/v1/workspaces/${id}/icon`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getToken()}` },
      body: form,
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.error);
    set((s) => ({
      workspaces: s.workspaces.map((w) => (w.id === id ? { ...w, ...data.data } : w)),
    }));
    return data.data;
  },

  deleteWorkspaceIcon: async (id) => {
    const ws = await authFetch(`/api/v1/workspaces/${id}/icon`, { method: 'DELETE' });
    set((s) => ({
      workspaces: s.workspaces.map((w) => (w.id === id ? { ...w, ...ws } : w)),
    }));
    return ws;
  },

  deleteWorkspace: async (id, { force = false } = {}) => {
    const qs = force ? '?force=1' : '';
    try {
      await authFetch(`/api/v1/workspaces/${id}${qs}`, { method: 'DELETE' });
      set((s) => ({
        workspaces: s.workspaces.filter((w) => w.id !== id),
        boards: s.boards.filter((b) => b.workspace_id !== id),
      }));
      return { deleted: true };
    } catch (err) {
      throw err;
    }
  },

  moveBoardToWorkspace: async (boardId, workspaceId) => {
    const board = await authFetch(`/api/v1/boards/${boardId}`, {
      method: 'PUT',
      body: JSON.stringify({ workspace_id: workspaceId }),
    });
    set((s) => ({
      boards: s.boards.map((b) =>
        b.id === boardId ? { ...b, workspace_id: workspaceId, folder_id: board.folder_id ?? null } : b
      ),
      currentBoard: s.currentBoard?.id === boardId
        ? { ...s.currentBoard, workspace_id: workspaceId, folder_id: board.folder_id ?? null }
        : s.currentBoard,
    }));
    return board;
  },

  fetchBoards: async (workspaceId) => {
    set({ loadingBoards: true });
    try {
      const wsBoards = await authFetch(`/api/v1/boards?workspace_id=${workspaceId}`);
      set((s) => {
        const other = s.boards.filter((b) => b.workspace_id !== workspaceId);
        // Reconcilia pra preservar referências de objetos que não mudaram.
        // Sem reconcile, cada fetchBoards gera um novo array e novos objetos board,
        // causando remount de DraggableBoardItem + BoardItemMenu e zerando o estado
        // local do menu (open/moving) — isso fechava o popup "Mover para..." imediatamente.
        const next = reconcileListById(s.boards, [...other, ...wsBoards]);
        return next !== s.boards ? { boards: next } : {};
      });
    } finally {
      set({ loadingBoards: false });
    }
  },

  fetchBoardData: async (id) => {
    set({ loadingBoard: true, boardError: null });
    try {
      const data = await authFetch(`/api/v1/boards/${id}/data`);
      const cvMap = {};
      for (const cv of data.cardValues ?? []) {
        cvMap[`${cv.card_id}:${cv.column_id}`] = cv.value;
      }
      const soMap = {};
      for (const so of data.statusOptions ?? []) {
        if (!soMap[so.column_id]) soMap[so.column_id] = [];
        soMap[so.column_id].push(so);
      }
      // Se ha mutacao de cards em voo (ou no grace period), preserva os cards
      // locais — fetchBoardData nao pode atropelar move/create/update otimista.
      // Demais campos (board, groups, columns, etc.) podem ser sobrescritos.
      const mutating = isCardsMutating();
      if (mutating) {
        dragLog('fetchBoardData: preserving local cards (mutation in flight)');
      }
      const prevCards = get().cards;
      set({
        currentBoard: data.board,
        // my_access so existe no contrato pos-M1a — ausente, useBoardCan cai
        // no fallback antigo (isOrgAdmin/boardMembers[].role).
        myAccess: data.my_access ?? null,
        groups: data.groups ?? [],
        columns: data.columns ?? [],
        cards: mutating ? prevCards : (data.cards ?? []),
        cardValues: cvMap,
        statusOptions: soMap,
        users: data.users ?? [],
        usersLoadedAt: Date.now(),
        editMode: false,
        boardError: null,
      });
      // registra em recentes apos sucesso
      get().pushRecent(data.board?.id ?? Number(id));
    } catch (err) {
      console.error('fetchBoardData failed:', err);
      set({
        currentBoard: null,
        myAccess: null,
        groups: [],
        columns: [],
        cards: [],
        cardValues: {},
        statusOptions: {},
        users: [],
        usersLoadedAt: 0,
        boardError: { status: err.status || 500, message: err.message },
      });
    } finally {
      set({ loadingBoard: false });
    }
  },

  fetchBoard: async (id) => {
    return get().fetchBoardData(id);
  },

  pollBoardData: async (id) => {
    if (get().isDragging) return;
    // Lock global: se ja ha um poll em voo pra esse board, skipa este tick.
    // Evita 2 polls concorrentes onde o gate de epoch so invalida 1.
    if (get()._pollInFlight) { dragLog('poll skipped: already in flight'); return; }
    set({ _pollInFlight: true });
    const epochAtStart = getCardValuesEpoch();
    const cardsEpochAtStart = getCardsEpoch();
    const mutatingAtStart = isCardsMutating();
    try {
      // Diretório de pessoas: só pede de novo depois de USERS_REFRESH_MS
      // (T11/§7.4). ?users=0 omite a consulta e a chave no backend.
      const wantUsers = Date.now() - get().usersLoadedAt >= USERS_REFRESH_MS;
      const data = await authFetch(`/api/v1/boards/${id}/data${wantUsers ? '' : '?users=0'}`);
      const cvMap = {};
      for (const cv of data.cardValues ?? []) {
        cvMap[`${cv.card_id}:${cv.column_id}`] = cv.value;
      }
      const soMap = {};
      for (const so of data.statusOptions ?? []) {
        if (!soMap[so.column_id]) soMap[so.column_id] = [];
        soMap[so.column_id].push(so);
      }
      // preserva valores locais de edits em voo para evitar race com PUT ainda pendente
      const pendingValues = get().pendingCardValueEdits;
      if (pendingValues.size > 0) {
        const localValues = get().cardValues;
        for (const key of pendingValues) {
          if (key in localValues) cvMap[key] = localValues[key];
        }
      }

      // preserva posicao/group_id local de cards com move em voo
      let cards = data.cards ?? [];
      const pendingMoves = get().pendingCardMoves;
      if (pendingMoves.size > 0) {
        const localCards = get().cards;
        const localById = new Map(localCards.map((c) => [c.id, c]));
        cards = cards.map((c) => (pendingMoves.has(c.id) ? (localById.get(c.id) ?? c) : c));
      }

      // Diff inteligente: preserva referencias de objetos/arrays nao modificados
      // pra evitar re-render desnecessario de toda a tabela em board grande.
      // Em caso de qualquer divergencia estrutural, retorna o valor novo (fallback seguro).
      const state = get();
      const nextGroups = reconcileListById(state.groups, data.groups ?? []);
      const nextColumns = reconcileListById(state.columns, data.columns ?? []);
      // Se houve mutacao de cards durante o poll (move/create/update/delete/archive),
      // descarta a lista do servidor e mantem o state local — mesma logica do
      // gate de cardValues. Cobre o caso em que pendingCardMoves ja foi limpo
      // antes do poll resolver, ou em que outras mutacoes (createCard, etc.)
      // nem entram no Set de pending.
      // Gate em 3 camadas:
      //  1. Mutex ativo (mutacao em voo OU grace period) → preserva local
      //  2. Estava mutando no inicio do poll (resposta provavelmente estale)
      //  3. Epoch bumpado durante o poll
      // Qualquer um dispara → mantem state.cards e descarta data.cards.
      const cardsChangedDuringPoll = getCardsEpoch() !== cardsEpochAtStart;
      const mutatingNow = isCardsMutating();
      const skipCards = cardsChangedDuringPoll || mutatingAtStart || mutatingNow;
      if (skipCards) {
        dragLog('poll: skip cards overwrite', { cardsChangedDuringPoll, mutatingAtStart, mutatingNow });
      }
      const nextCards = skipCards
        ? state.cards
        : reconcileListById(state.cards, cards);
      // data.users só existe quando wantUsers pediu (senão a chave nem vem);
      // preserva o diretório local em vez de tratar ausência como lista
      // vazia (T11) — antes, `data.users ?? []` apagava o diretório a cada tique.
      const nextUsers = Array.isArray(data.users)
        ? reconcileListById(state.users, data.users)
        : state.users;
      // Se houve mutacao de cardValues durante o poll (entre fetch e resolve),
      // descarta cvMap (provavelmente estale) e mantem o state local.
      // Cobre o caso em que upsertCardValue rodou e removeu do pendingCardValueEdits
      // antes do poll resolver — pendingCardValueEdits sozinho perderia esse edit.
      const cardValuesChangedDuringPoll = getCardValuesEpoch() !== epochAtStart;
      const nextCardValues = cardValuesChangedDuringPoll
        ? state.cardValues
        : reconcileCardValues(state.cardValues, cvMap);
      const nextStatusOptions = reconcileStatusOptions(state.statusOptions, soMap);

      // currentBoard: shallow compare (objeto plano)
      const nextCurrentBoard = shallowEqual(state.currentBoard, data.board) ? state.currentBoard : data.board;
      // my_access: so existe no contrato pos-M1a. Ausente na resposta (backend
      // M1a), preserva o que ja estava (null desde o fetchBoardData inicial).
      const nextMyAccess = Object.prototype.hasOwnProperty.call(data, 'my_access')
        ? data.my_access ?? null
        : state.myAccess;

      // Patch parcial: so atualiza chaves cujo valor de fato mudou.
      // Isso evita notificar subscribers que assinam apenas chaves intactas.
      const patch = {};
      if (nextCurrentBoard !== state.currentBoard) patch.currentBoard = nextCurrentBoard;
      if (!sameMyAccess(state.myAccess, nextMyAccess)) patch.myAccess = nextMyAccess;
      if (nextGroups !== state.groups) patch.groups = nextGroups;
      if (nextColumns !== state.columns) patch.columns = nextColumns;
      if (nextCards !== state.cards) patch.cards = nextCards;
      if (nextCardValues !== state.cardValues) patch.cardValues = nextCardValues;
      if (nextStatusOptions !== state.statusOptions) patch.statusOptions = nextStatusOptions;
      if (nextUsers !== state.users) patch.users = nextUsers;
      if (Array.isArray(data.users)) patch.usersLoadedAt = Date.now();
      if (Object.keys(patch).length > 0) set(patch);
    } catch (err) {
      // durante polling: se perdeu o acesso, marca erro pra UI reagir
      if (err?.status === 403 || err?.status === 404) {
        set({
          currentBoard: null,
          myAccess: null,
          groups: [],
          columns: [],
          cards: [],
          cardValues: {},
          statusOptions: {},
          users: [],
          usersLoadedAt: 0,
          boardError: { status: err.status, message: err.message },
        });
        get().stopPolling();
      }
    } finally {
      set({ _pollInFlight: false });
    }
  },

  startPolling: (id, intervalMs = 30000) => {
    get().stopPolling();
    const tick = () => {
      get().pollBoardData(id);
      get().fetchBoardMembers(id).catch(() => {});
    };
    const timer = setInterval(tick, intervalMs);
    set({ _pollTimer: timer });
  },

  stopPolling: () => {
    const { _pollTimer } = get();
    if (_pollTimer) { clearInterval(_pollTimer); set({ _pollTimer: null }); }
  },

  refreshSidebar: async () => {
    try {
      // Fetch workspaces sem set() direto — aplica reconcile pra estabilizar referencia.
      // Sem reconcile, cada poll gera um array novo mesmo sem mudanca de conteudo,
      // o que dispara o useEffect[workspaces] em AppLayout e pode mudar activeWsId,
      // desmontando WorkspaceSection (key={activeWsId}) e fechando menus abertos.
      set({ loadingWorkspaces: true });
      try {
        const fresh = await authFetch('/api/v1/workspaces');
        set((s) => {
          const next = reconcileListById(s.workspaces, fresh);
          return next !== s.workspaces ? { workspaces: next, loadingWorkspaces: false } : { loadingWorkspaces: false };
        });
      } finally {
        set({ loadingWorkspaces: false });
      }
      const wsList = get().workspaces || [];
      await Promise.all(wsList.map((w) => get().fetchBoards(w.id)));
    } catch (err) {
      console.error('refreshSidebar failed:', err);
    }
  },

  startSidebarPolling: (intervalMs = 15000) => {
    get().stopSidebarPolling();
    const tick = () => { get().refreshSidebar(); };
    const timer = setInterval(tick, intervalMs);
    set({ _sidebarPollTimer: timer });
  },

  stopSidebarPolling: () => {
    const { _sidebarPollTimer } = get();
    if (_sidebarPollTimer) { clearInterval(_sidebarPollTimer); set({ _sidebarPollTimer: null }); }
  },

  fetchBoardMembers: async (boardId) => {
    const data = await authFetch(`/api/v1/boards/${boardId}/members`);
    set({ boardMembers: data });
  },

  upsertBoardMember: async (boardId, userId, role, allowedGroups) => {
    const data = await authFetch(`/api/v1/boards/${boardId}/members`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, role, allowed_groups: allowedGroups }),
    });
    set((s) => {
      const existing = s.boardMembers.findIndex((m) => m.user_id === userId);
      if (existing >= 0) {
        const next = [...s.boardMembers];
        next[existing] = { ...next[existing], ...data };
        return { boardMembers: next };
      }
      return { boardMembers: [...s.boardMembers, data] };
    });
    return data;
  },

  updateBoardMember: async (boardId, userId, fields) => {
    const data = await authFetch(`/api/v1/boards/${boardId}/members/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(fields),
    });
    set((s) => ({
      boardMembers: s.boardMembers.map((m) => m.user_id === userId ? { ...m, ...data } : m),
    }));
  },

  removeBoardMember: async (boardId, userId) => {
    await authFetch(`/api/v1/boards/${boardId}/members/${userId}`, { method: 'DELETE' });
    set((s) => ({ boardMembers: s.boardMembers.filter((m) => m.user_id !== userId) }));
  },

  // ================= ACESSO AO QUADRO (contrato M1 — GET/PUT/DELETE /boards/:id/access) =================
  fetchBoardAccess: async (boardId) => {
    set({ loadingBoardAccess: true, boardAccessError: null });
    try {
      const data = await authFetch(`/api/v1/boards/${boardId}/access`);
      const list = Array.isArray(data) ? data : [];
      set({ boardAccess: list });
      return list;
    } catch (err) {
      set({ boardAccessError: err.message || 'Falha ao carregar acesso do quadro' });
      throw err;
    } finally {
      set({ loadingBoardAccess: false });
    }
  },

  // Grava uma excecao de nivel para a pessoa (PUT). O contrato devolve a
  // entrada resultante { user, level, source } — atualiza so essa linha em
  // vez de reconsultar a lista inteira.
  setBoardAccessLevel: async (boardId, userId, level) => {
    const entry = await authFetch(`/api/v1/boards/${boardId}/access/${userId}`, {
      method: 'PUT',
      body: JSON.stringify({ level }),
    });
    set((s) => {
      const idx = s.boardAccess.findIndex((a) => a.user.id === userId);
      if (idx === -1) return { boardAccess: [...s.boardAccess, entry] };
      const next = [...s.boardAccess];
      next[idx] = entry;
      return { boardAccess: next };
    });
    return entry;
  },

  // "Voltar ao padrao": remove a excecao direta no quadro (DELETE). O contrato
  // devolve a entrada herdada resultante, ou null se a pessoa nao tem mais
  // nenhum acesso (nem herdado) — nesse caso ela sai da lista.
  clearBoardAccessOverride: async (boardId, userId) => {
    const entry = await authFetch(`/api/v1/boards/${boardId}/access/${userId}`, { method: 'DELETE' });
    set((s) => {
      if (!entry) {
        return { boardAccess: s.boardAccess.filter((a) => a.user.id !== userId) };
      }
      const idx = s.boardAccess.findIndex((a) => a.user.id === userId);
      if (idx === -1) return { boardAccess: [...s.boardAccess, entry] };
      const next = [...s.boardAccess];
      next[idx] = entry;
      return { boardAccess: next };
    });
    return entry;
  },

  setBoardVisibility: async (boardId, visibility) => {
    const board = await authFetch(`/api/v1/boards/${boardId}/visibility`, {
      method: 'PUT',
      body: JSON.stringify({ visibility }),
    });
    set((s) => ({
      currentBoard: s.currentBoard?.id === boardId ? { ...s.currentBoard, ...board } : s.currentBoard,
      boards: s.boards.map((b) => (b.id === boardId ? { ...b, ...board } : b)),
    }));
    return board;
  },

  // --- senha de abertura do quadro ("Proteger") -------------------------
  //
  // A senha so existe em transito: vai no corpo do pedido e nunca e guardada
  // na store, em localStorage ou em sessionStorage. O que persiste por aba e
  // apenas "este quadro ja foi desbloqueado" (utils/boardUnlock.js).

  // `userId` e `version` vem de quem chama (BoardUnlockGate): a marca guardada
  // e a trinca quadro+usuario+geracao, e `version` e a geracao que ACABOU de
  // ser conferida — nao a que estiver na store quando o pedido voltar.
  unlockBoard: async (boardId, password, { userId, version }) => {
    await authFetch(`/api/v1/boards/${boardId}/unlock`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
    markBoardUnlocked(boardId, userId, version);
    return true;
  },

  setBoardProtection: async (boardId, { password, currentPassword }) => {
    const data = await authFetch(`/api/v1/boards/${boardId}/protection`, {
      method: 'PUT',
      body: JSON.stringify({
        password,
        ...(currentPassword ? { current_password: currentPassword } : {}),
      }),
    });
    // A geracao nova entra no currentBoard, e com isso a marca de desbloqueio
    // antiga deixa de casar: quem acabou de cadastrar ou trocar a senha
    // tambem digita, na hora. As OUTRAS abas chegam na mesma geracao pelo
    // polling (board.model.js#getById manda protection_version) — era isso
    // que faltava pra troca de senha re-trancar aba que nao foi a que trocou.
    set((s) => ({
      currentBoard: s.currentBoard?.id === Number(boardId)
        ? { ...s.currentBoard, is_protected: true, protection_version: data?.protection_version ?? null }
        : s.currentBoard,
      boards: s.boards.map((b) => (b.id === Number(boardId) ? { ...b, is_protected: true } : b)),
    }));
  },

  removeBoardProtection: async (boardId, { currentPassword } = {}) => {
    await authFetch(`/api/v1/boards/${boardId}/protection`, {
      method: 'DELETE',
      ...(currentPassword ? { body: JSON.stringify({ current_password: currentPassword }) } : {}),
    });
    set((s) => ({
      currentBoard: s.currentBoard?.id === Number(boardId)
        ? { ...s.currentBoard, is_protected: false, protection_version: null }
        : s.currentBoard,
      boards: s.boards.map((b) => (b.id === Number(boardId) ? { ...b, is_protected: false } : b)),
    }));
  },

  fetchRoles: async () => {
    set({ loadingRoles: true });
    try {
      const roles = await authFetch('/api/v1/roles');
      const list = Array.isArray(roles) ? roles : [];
      set({ roles: list });
      return list;
    } finally {
      set({ loadingRoles: false });
    }
  },

  createBoard: async (name, workspaceId) => {
    const board = await authFetch('/api/v1/boards', {
      method: 'POST',
      body: JSON.stringify({ name, workspace_id: workspaceId }),
    });
    set((s) => ({ boards: [...s.boards, board] }));
    return board;
  },

  updateBoard: async (id, fields) => {
    const board = await authFetch(`/api/v1/boards/${id}`, {
      method: 'PUT',
      body: JSON.stringify(fields),
    });
    set((s) => ({
      // Guard por id: `updateBoard` tambem renomeia quadro da barra lateral,
      // que nao e o quadro aberto. Sem o guard, renomear OUTRO quadro
      // substituia o currentBoard pelo renomeado — e, num quadro protegido e
      // trancado, o gate sumia e os cartoes apareciam ate o proximo poll (e o
      // inverso tambem: renomear um quadro protegido trancava a tela de um
      // quadro sem senha).
      currentBoard: s.currentBoard?.id === Number(id) ? board : s.currentBoard,
      boards: s.boards.map((b) => (b.id === Number(id) ? { ...b, ...board } : b)),
    }));
    return board;
  },

  updateBoardConfig: async (boardId, configPatch) => {
    const state = get();
    const currentConfig = state.currentBoard?.config ?? {};
    const merged = {
      ...currentConfig,
      ...configPatch,
      toolbarVisibility: {
        ...(currentConfig.toolbarVisibility ?? {}),
        ...(configPatch.toolbarVisibility ?? {}),
      },
    };
    // Optimistic update. Guard por id pelo mesmo motivo de updateBoard acima:
    // nunca escrever no currentBoard a partir de um pedido sobre outro quadro.
    const isCurrent = (st) => st.currentBoard?.id === Number(boardId);
    set((s) => ({
      currentBoard: isCurrent(s) ? { ...s.currentBoard, config: merged } : s.currentBoard,
    }));
    try {
      const board = await authFetch(`/api/v1/boards/${boardId}`, {
        method: 'PUT',
        body: JSON.stringify({ config: merged }),
      });
      set((s) => ({
        currentBoard: isCurrent(s) ? { ...s.currentBoard, config: board.config ?? merged } : s.currentBoard,
      }));
      return board;
    } catch (err) {
      // Reverte em caso de erro
      set((s) => ({
        currentBoard: isCurrent(s) ? { ...s.currentBoard, config: currentConfig } : s.currentBoard,
      }));
      throw err;
    }
  },

  deleteBoard: async (id) => {
    await authFetch(`/api/v1/boards/${id}`, { method: 'DELETE' });
    const { currentBoard } = get();
    set((s) => ({
      boards: s.boards.filter((b) => b.id !== id),
      currentBoard: currentBoard?.id === id ? null : currentBoard,
      groups: currentBoard?.id === id ? [] : s.groups,
    }));
  },

  archiveBoard: async (id) => {
    await authFetch(`/api/v1/boards/${id}/archive`, { method: 'PUT' });
    const { currentBoard } = get();
    set((s) => ({
      boards: s.boards.filter((b) => b.id !== id),
      currentBoard: currentBoard?.id === id ? null : currentBoard,
      groups: currentBoard?.id === id ? [] : s.groups,
    }));
  },

  restoreBoard: async (id) => {
    const board = await authFetch(`/api/v1/boards/${id}/restore`, { method: 'PUT' });
    set((s) => ({ boards: [...s.boards, board] }));
    return board;
  },

  fetchArchivedBoards: async (workspaceId) => {
    return authFetch(`/api/v1/boards/workspace/${workspaceId}/archived`);
  },

  duplicateBoard: async (id) => {
    const board = await authFetch(`/api/v1/boards/${id}/duplicate`, {
      method: 'POST',
    });
    set((s) => ({ boards: [board, ...s.boards] }));
    return board;
  },

  createGroup: async (boardId, name, color, opts = {}) => {
    const group = await authFetch(`/api/v1/boards/${boardId}/groups`, {
      method: 'POST',
      body: JSON.stringify({ name, color, is_divider: !!opts.isDivider }),
    });
    set((s) => ({ groups: [...s.groups, group] }));
    return group;
  },

  updateGroup: async (id, fields) => {
    const group = await authFetch(`/api/v1/groups/${id}`, {
      method: 'PUT',
      body: JSON.stringify(fields),
    });
    set((s) => ({
      groups: s.groups.map((g) => (g.id === id ? group : g)),
    }));
    return group;
  },

  deleteGroup: async (id) => {
    await authFetch(`/api/v1/groups/${id}`, { method: 'DELETE' });
    set((s) => ({
      groups: s.groups.filter((g) => g.id !== id),
      cards: s.cards.filter((c) => c.group_id !== id),
    }));
  },

  archiveGroup: async (id) => {
    await authFetch(`/api/v1/groups/${id}/archive`, { method: 'PUT' });
    set((s) => ({
      groups: s.groups.filter((g) => g.id !== id),
      cards: s.cards.filter((c) => c.group_id !== id),
    }));
  },

  restoreGroup: async (id) => {
    const group = await authFetch(`/api/v1/groups/${id}/restore`, { method: 'PUT' });
    set((s) => ({ groups: [...s.groups, group] }));
    return group;
  },

  moveGroupToBoard: async (groupId, targetBoardId) => {
    const result = await authFetch(`/api/v1/groups/${groupId}/move`, {
      method: 'POST',
      body: JSON.stringify({ targetBoardId }),
    });
    // Remove o grupo e seus cards do board atual
    set((s) => ({
      groups: s.groups.filter((g) => g.id !== groupId),
      cards: s.cards.filter((c) => c.group_id !== groupId),
    }));
    return result;
  },

  fetchArchivedGroups: async (boardId) => {
    return authFetch(`/api/v1/boards/${boardId}/groups/archived`);
  },

  createColumn: async (boardId, name, type, config) => {
    const column = await authFetch(`/api/v1/boards/${boardId}/columns`, {
      method: 'POST',
      body: JSON.stringify({ name, type, config }),
    });
    set((s) => ({ columns: [...s.columns, column] }));
    if (type === 'status') {
      const defaults = [
        { label: 'Trabalhando', color: '#fdab3d', position: 0 },
        { label: 'Concluído', color: '#00c875', position: 1 },
        { label: 'Parado', color: '#e44258', position: 2 },
      ];
      const created = [];
      for (const opt of defaults) {
        const o = await authFetch(`/api/v1/columns/${column.id}/status-options`, {
          method: 'POST',
          body: JSON.stringify(opt),
        });
        created.push(o);
      }
      set((s) => ({
        statusOptions: { ...s.statusOptions, [column.id]: created },
      }));
    }
    return column;
  },

  updateColumn: async (id, fields) => {
    const column = await authFetch(`/api/v1/columns/${id}`, {
      method: 'PUT',
      body: JSON.stringify(fields),
    });
    set((s) => ({
      columns: s.columns.map((c) => (c.id === id ? column : c)),
    }));
    return column;
  },

  // M3c: define/remove o papel de workflow da coluna ('primary'|'secondary'|null).
  // Erro 409 (já existe outra primary no quadro) propaga pra quem chama tratar
  // com mensagem amigável — não é capturado aqui de propósito.
  setColumnWorkflowRole: async (id, workflow_role) => {
    const column = await authFetch(`/api/v1/columns/${id}/workflow-role`, {
      method: 'PATCH',
      body: JSON.stringify({ workflow_role }),
    });
    set((s) => ({
      columns: s.columns.map((c) => (c.id === id ? column : c)),
    }));
    return column;
  },

  // M3c: vincula (ou remove, com null) a coluna de pessoa que resolve o
  // responsável desta coluna de status. Erro 400 (coluna inválida/outro
  // quadro/não é pessoa) propaga pra quem chama tratar.
  setColumnAssigneeColumn: async (id, assignee_column_id) => {
    const column = await authFetch(`/api/v1/columns/${id}/assignee-column`, {
      method: 'PATCH',
      body: JSON.stringify({ assignee_column_id }),
    });
    set((s) => ({
      columns: s.columns.map((c) => (c.id === id ? column : c)),
    }));
    return column;
  },

  reorderColumns: async (boardId, columnIds) => {
    const snapshot = get().columns;
    set((s) => ({
      columns: s.columns.map((c) => {
        const idx = columnIds.indexOf(c.id);
        return idx >= 0 ? { ...c, position: idx } : c;
      }),
    }));
    try {
      await authFetch(`/api/v1/boards/${boardId}/columns/reorder`, {
        method: 'PUT',
        body: JSON.stringify({ columnIds }),
      });
    } catch (err) {
      set({ columns: snapshot });
      throw err;
    }
  },

  reorderGroups: async (boardId, groupIds) => {
    const snapshot = get().groups;
    set((s) => ({
      groups: s.groups.map((g) => {
        const idx = groupIds.indexOf(g.id);
        return idx >= 0 ? { ...g, position: idx } : g;
      }),
    }));
    try {
      await authFetch(`/api/v1/boards/${boardId}/groups/reorder`, {
        method: 'PUT',
        body: JSON.stringify({ groupIds }),
      });
    } catch (err) {
      set({ groups: snapshot });
      throw err;
    }
  },

  deleteColumn: async (id) => {
    await authFetch(`/api/v1/columns/${id}`, { method: 'DELETE' });
    set((s) => ({
      columns: s.columns.filter((c) => c.id !== id),
    }));
  },

  changeColumnType: async (id, newType) => {
    const column = await authFetch(`/api/v1/columns/${id}/type`, {
      method: 'PUT',
      body: JSON.stringify({ type: newType }),
    });
    // Atualiza a coluna e limpa statusOptions se o novo tipo não é status
    set((s) => {
      const next = { columns: s.columns.map((c) => (c.id === id ? column : c)) };
      if (newType !== 'status') {
        const so = { ...s.statusOptions };
        delete so[id];
        next.statusOptions = so;
      }
      // Limpa card_values locais da coluna — o servidor já migrou,
      // o próximo poll vai trazer os valores corretos
      const nextCardValues = {};
      for (const [k, v] of Object.entries(s.cardValues)) {
        if (!k.endsWith(`:${id}`)) nextCardValues[k] = v;
      }
      next.cardValues = nextCardValues;
      return next;
    });
    return column;
  },

  createCard: async (groupId, title, opts = {}) => {
    const body = { title, is_divider: !!opts.isDivider };
    if ('position' in opts) body.position = opts.position;
    const card = await authFetch(`/api/v1/groups/${groupId}/cards`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    bumpCardsEpoch();
    set((s) => ({ cards: [...s.cards, card] }));
    return card;
  },

  // M2b: duplicar passou a ser 1 requisição ao servidor (antes: criar +
  // 1 upsert de valor por coluna preenchida, cada um virando o próprio
  // evento sintético de mudança de valor). `batchId` opcional — quando o
  // chamador duplica vários cartões num só gesto, todos entram no mesmo lote.
  duplicateCard: async (id, { batchId } = {}) => {
    const { values, ...card } = await authFetch(`/api/v1/cards/${id}/duplicate`, {
      method: 'POST',
      ...(batchId ? { headers: { 'X-Batch-Id': batchId } } : {}),
    });
    bumpCardsEpoch();
    set((s) => {
      const nextCardValues = { ...s.cardValues };
      for (const v of values || []) {
        nextCardValues[`${card.id}:${v.column_id}`] = v.value;
      }
      return { cards: [...s.cards, card], cardValues: nextCardValues };
    });
    return card;
  },

  updateCard: async (id, fields) => {
    const card = await authFetch(`/api/v1/cards/${id}`, {
      method: 'PUT',
      body: JSON.stringify(fields),
    });
    // mescla — nao sobrescreve campos computados como comment_count que o PUT nao retorna
    bumpCardsEpoch();
    set((s) => ({
      cards: s.cards.map((c) => (c.id === id ? { ...c, ...card } : c)),
    }));
    return card;
  },

  deleteCard: async (id) => {
    await authFetch(`/api/v1/cards/${id}`, { method: 'DELETE' });
    bumpCardsEpoch();
    set((s) => ({
      cards: s.cards.filter((c) => c.id !== id),
    }));
  },

  archiveCard: async (id) => {
    await authFetch(`/api/v1/cards/${id}/archive`, { method: 'PUT' });
    bumpCardsEpoch();
    set((s) => ({ cards: s.cards.filter((c) => c.id !== id) }));
  },

  // X-Batch-Id (M2b, item 5): um só pro gesto inteiro — sem ele, 40 cartões
  // arquivados de uma vez viravam 40 card.archived avulsos no histórico,
  // indistinguíveis de 40 cliques separados.
  bulkArchiveCards: async (ids) => {
    const idSet = new Set(ids);
    bumpCardsEpoch();
    set((s) => ({ cards: s.cards.filter((c) => !idSet.has(c.id)) }));
    const batchId = crypto.randomUUID();
    await Promise.all(ids.map((id) => authFetch(`/api/v1/cards/${id}/archive`, {
      method: 'PUT', headers: { 'X-Batch-Id': batchId },
    })));
    bumpCardsEpoch();
  },

  bulkUnarchiveCards: async (ids) => {
    const batchId = crypto.randomUUID();
    const restored = await Promise.all(ids.map(async (id) => {
      const card = await authFetch(`/api/v1/cards/${id}/unarchive`, {
        method: 'PUT', headers: { 'X-Batch-Id': batchId },
      });
      if (card && card.comment_count === undefined) {
        try {
          const list = await authFetch(`/api/v1/cards/${id}/comments`);
          card.comment_count = Array.isArray(list) ? list.length : 0;
        } catch { card.comment_count = 0; }
      }
      return card;
    }));
    bumpCardsEpoch();
    set((s) => ({ cards: [...s.cards, ...restored], selectedCardIds: ids }));
  },

  unarchiveCard: async (id) => {
    const card = await authFetch(`/api/v1/cards/${id}/unarchive`, { method: 'PUT' });
    // fallback: se o backend nao retorna comment_count aqui, busca via count individual
    if (card && card.comment_count === undefined) {
      try {
        const list = await authFetch(`/api/v1/cards/${id}/comments`);
        card.comment_count = Array.isArray(list) ? list.length : 0;
      } catch {
        card.comment_count = 0;
      }
    }
    bumpCardsEpoch();
    set((s) => ({ cards: [...s.cards, card] }));
    return card;
  },

  fetchArchivedCards: async (boardId) => {
    return authFetch(`/api/v1/boards/${boardId}/cards/archived`);
  },

  fetchGlobalTrash: async () => {
    return authFetch('/api/v1/trash');
  },

  moveCardLocal: (cardId, newGroupId, newPosition) => {
    bumpCardsEpoch();
    set((s) => {
      const card = s.cards.find((c) => c.id === cardId);
      if (!card) return {};
      const oldGroupId = card.group_id;
      const oldPosition = card.position ?? 0;

      const rebuilt = s.cards.map((c) => {
        if (c.id === cardId) return { ...c, group_id: newGroupId, position: newPosition };

        if (oldGroupId === newGroupId) {
          if (c.group_id !== oldGroupId) return c;
          const p = c.position ?? 0;
          if (oldPosition < newPosition) {
            if (p > oldPosition && p <= newPosition) return { ...c, position: p - 1 };
          } else if (oldPosition > newPosition) {
            if (p >= newPosition && p < oldPosition) return { ...c, position: p + 1 };
          }
          return c;
        }

        if (c.group_id === oldGroupId && (c.position ?? 0) > oldPosition) {
          return { ...c, position: (c.position ?? 0) - 1 };
        }
        if (c.group_id === newGroupId && (c.position ?? 0) >= newPosition) {
          return { ...c, position: (c.position ?? 0) + 1 };
        }
        return c;
      });
      return { cards: rebuilt };
    });
  },

  moveCard: async (cardId, newGroupId, newPosition) => {
    const snapshot = get().cards;
    beginCardsMutation();
    dragLog('moveCard: begin', { cardId, newGroupId, newPosition });
    get().moveCardLocal(cardId, newGroupId, newPosition);
    set((s) => ({ pendingCardMoves: new Set([...s.pendingCardMoves, cardId]) }));
    try {
      await authFetch(`/api/v1/cards/${cardId}/move`, {
        method: 'PUT',
        body: JSON.stringify({ group_id: newGroupId, position: newPosition }),
      });
      dragLog('moveCard: PUT ok', { cardId });
    } catch (err) {
      bumpCardsEpoch();
      set({ cards: snapshot });
      dragLog('moveCard: PUT failed, rolled back', { cardId, err: err?.message });
      throw err;
    } finally {
      bumpCardsEpoch();
      set((s) => {
        const next = new Set(s.pendingCardMoves);
        next.delete(cardId);
        return { pendingCardMoves: next };
      });
      endCardsMutation();
      dragLog('moveCard: end', { cardId });
    }
  },

  fetchAllBoards: async () => {
    return authFetch('/api/v1/boards/all');
  },

  // Move múltiplos cards via endpoint atômico /bulk-move com snapshot/rollback,
  // marcação de pendentes e suporte a cross-board.
  // Aceita Set ou Array em cardIds. Retorna { originalGroups: Map<cardId, groupId> } para undo.
  moveSelectedCards: async (cardIds, targetGroupId, isCrossBoard) => {
    const idArray = Array.isArray(cardIds) ? [...cardIds] : [...cardIds];
    if (idArray.length === 0) return { originalGroups: new Map() };

    const idSet = new Set(idArray);
    const snapshot = get().cards;

    // Captura grupo original de cada card pra permitir undo
    const originalGroups = new Map();
    for (const c of snapshot) {
      if (idSet.has(c.id)) originalGroups.set(c.id, c.group_id);
    }

    // Optimistic update: cross-board remove os cards do quadro atual,
    // same-board move pro grupo destino na cauda.
    beginCardsMutation();
    bumpCardsEpoch();
    if (isCrossBoard) {
      set((s) => ({ cards: s.cards.filter((c) => !idSet.has(c.id)) }));
    } else {
      set((s) => {
        const targetTailStart = s.cards.filter(
          (c) => c.group_id === targetGroupId && !idSet.has(c.id)
        ).length;
        let i = 0;
        return {
          cards: s.cards.map((c) =>
            idSet.has(c.id)
              ? { ...c, group_id: targetGroupId, position: targetTailStart + i++ }
              : c
          ),
        };
      });
    }

    // Marca pendentes
    set((s) => ({ pendingCardMoves: new Set([...s.pendingCardMoves, ...idArray]) }));

    try {
      // Same-board: insere ao final do grupo destino (excluindo os próprios) usando o snapshot local.
      // Cross-board: o snapshot só conhece o board atual, então delega o cálculo do "fim" pro backend
      // enviando startPosition=null.
      const destOthersCount = snapshot.filter(
        (c) => c.group_id === targetGroupId && !idSet.has(c.id)
      ).length;
      const startPosition = isCrossBoard ? null : destOthersCount;

      // X-Batch-Id (M2b, item 5): o backend já sabia agrupar por ele
      // (bulkMoveCards no servidor cai pra um UUID próprio se o cabeçalho
      // não vier) — faltava só o cliente mandar, pra 40 cartões arrastados
      // juntos aparecerem como 1 movimento no histórico, não 40.
      await authFetch('/api/v1/cards/bulk-move', {
        method: 'POST',
        headers: { 'X-Batch-Id': crypto.randomUUID() },
        body: JSON.stringify({
          cardIds: idArray.map(Number),
          targetGroupId: Number(targetGroupId),
          startPosition,
        }),
      });

      return { originalGroups };
    } catch (err) {
      // Rollback completo do snapshot em qualquer falha
      bumpCardsEpoch();
      set({ cards: snapshot });
      throw err;
    } finally {
      bumpCardsEpoch();
      set((s) => {
        const next = new Set(s.pendingCardMoves);
        for (const id of idArray) next.delete(id);
        return { pendingCardMoves: next };
      });
      endCardsMutation();
    }
  },

  // Move múltiplos cards para um grupo numa posição específica, mantendo ordem relativa.
  // cardIdsOrdered: array de ids já ordenados pela posição visual atual.
  // startPosition: índice de inserção no grupo destino (excluindo os próprios cards selecionados).
  moveCardsBatch: (cardIdsOrdered, targetGroupId, startPosition) => {
    const idSet = new Set(cardIdsOrdered);

    // Atualização otimista local em uma única transação Zustand
    bumpCardsEpoch();
    set((s) => {
      // Recalcula posições dos cards não-selecionados no grupo destino
      const othersInTarget = s.cards
        .filter((c) => c.group_id === targetGroupId && !idSet.has(c.id))
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

      // Abre espaço: cards que estavam na posição >= startPosition sobem
      const othersWithGap = othersInTarget.map((c, idx) => {
        const realIdx = idx; // posição original no array ordenado sem selecionados
        const insertedBefore = realIdx >= startPosition ? cardIdsOrdered.length : 0;
        return { ...c, position: realIdx + insertedBefore };
      });

      // Constrói mapa de id -> nova posição para os outros
      const posMap = new Map(othersWithGap.map((c) => [c.id, c.position]));

      // Posições para os cards sendo movidos
      cardIdsOrdered.forEach((id, i) => {
        posMap.set(id, startPosition + i);
      });

      const rebuilt = s.cards.map((c) => {
        if (posMap.has(c.id)) {
          const newPos = posMap.get(c.id);
          if (idSet.has(c.id)) return { ...c, group_id: targetGroupId, position: newPos };
          return { ...c, position: newPos };
        }
        return c;
      });

      return { cards: rebuilt };
    });
  },

  moveCardsBatchAsync: async (cardIdsOrdered, targetGroupId, startPosition, snapshot) => {
    // Marca todos os cards como pendentes antes de iniciar a request
    beginCardsMutation();
    dragLog('moveCardsBatchAsync: begin', { count: cardIdsOrdered.length, targetGroupId });
    bumpCardsEpoch();
    set((s) => ({ pendingCardMoves: new Set([...s.pendingCardMoves, ...cardIdsOrdered]) }));
    try {
      // Endpoint bulk atômico: move N cards em uma única transação Postgres
      await authFetch('/api/v1/cards/bulk-move', {
        method: 'POST',
        body: JSON.stringify({
          cardIds: cardIdsOrdered,
          targetGroupId,
          startPosition,
        }),
      });
    } catch (err) {
      bumpCardsEpoch();
      set({ cards: snapshot });
      throw err;
    } finally {
      // Remove do pending independente de sucesso ou erro
      bumpCardsEpoch();
      set((s) => {
        const next = new Set(s.pendingCardMoves);
        for (const id of cardIdsOrdered) next.delete(id);
        return { pendingCardMoves: next };
      });
      endCardsMutation();
      dragLog('moveCardsBatchAsync: end');
    }
  },

  upsertCardValue: async (cardId, columnId, value) => {
    const { selectedCardIds } = get();
    const isBulk = selectedCardIds.includes(cardId) && selectedCardIds.length > 1;
    const targetIds = isBulk ? selectedCardIds : [cardId];
    const keys = targetIds.map((id) => `${id}:${columnId}`);

    // bump epoch ANTES do optimistic update — qualquer poll cujo fetch tenha
    // iniciado antes daqui vai detectar a mutacao no momento de aplicar e
    // descartar o cvMap estale.
    bumpCardValuesEpoch();

    // Valor de antes de cada chave, para desfazer o optimistic update se o
    // servidor recusar. Sem isto, o valor recusado ficava colado na tela até
    // o próximo poll apagá-lo em silêncio — e o usuário não tinha como saber
    // que não foi salvo.
    const anterior = Object.fromEntries(keys.map((k) => [k, get().cardValues[k]]));

    // optimistic update
    set((s) => {
      const next = { ...s.cardValues };
      for (const id of targetIds) next[`${id}:${columnId}`] = value;
      return { cardValues: next };
    });

    // marca chaves como pendentes (nova referencia — nunca mutar Set in-place)
    set((s) => ({ pendingCardValueEdits: new Set([...s.pendingCardValueEdits, ...keys]) }));

    try {
      await Promise.all(
        targetIds.map((id) =>
          authFetch(`/api/v1/cards/${id}/columns/${columnId}/value`, {
            method: 'PUT',
            body: JSON.stringify({ column_id: columnId, value }),
          })
        )
      );
    } catch (err) {
      // Devolve a célula ao valor anterior AGORA, em vez de esperar o poll.
      set((s) => {
        const next = { ...s.cardValues };
        for (const k of keys) {
          if (anterior[k] === undefined) delete next[k];
          else next[k] = anterior[k];
        }
        return { cardValues: next };
      });
      // 404 aqui quer dizer coluna que não pertence ao quadro do cartão (a
      // conferência que o M1b acrescentou): acontece com a tela
      // desatualizada, depois de o cartão ou a coluna mudarem de lugar.
      const recarregar = err?.status === 404 || err?.status === 403;
      useToastStore.getState().showError(
        recarregar
          ? 'Não foi possível salvar: este campo não existe mais neste quadro. Recarregue a página.'
          : `Não foi possível salvar: ${err?.message || 'erro inesperado'}`
      );
    } finally {
      // remove chaves do pending independente de sucesso ou erro
      // se falhou: o valor anterior já foi restaurado acima e o proximo poll
      // confirma o que esta no servidor
      set((s) => {
        const next = new Set(s.pendingCardValueEdits);
        for (const k of keys) next.delete(k);
        return { pendingCardValueEdits: next };
      });
      // bump tambem no fim — cobre o caso de poll que iniciou enquanto o PUT
      // estava em voo e vai resolver depois do finally limpar pendingCardValueEdits.
      bumpCardValuesEpoch();
    }
  },

  toggleCardSelection: (id) => set((s) => {
    const set_ = new Set(s.selectedCardIds);
    if (set_.has(id)) set_.delete(id); else set_.add(id);
    return { selectedCardIds: [...set_] };
  }),

  selectCardGroup: (ids, check) => set((s) => {
    const set_ = new Set(s.selectedCardIds);
    for (const id of ids) {
      if (check) set_.add(id); else set_.delete(id);
    }
    return { selectedCardIds: [...set_] };
  }),

  clearCardSelection: () => set({ selectedCardIds: [] }),

  createStatusOption: async (columnId, label, color) => {
    const option = await authFetch(`/api/v1/columns/${columnId}/status-options`, {
      method: 'POST',
      body: JSON.stringify({ label, color }),
    });
    set((s) => ({
      statusOptions: {
        ...s.statusOptions,
        [columnId]: [...(s.statusOptions[columnId] || []), option],
      },
    }));
    return option;
  },

  updateStatusOption: async (id, columnId, fields) => {
    const option = await authFetch(`/api/v1/status-options/${id}`, {
      method: 'PUT',
      body: JSON.stringify(fields),
    });
    set((s) => ({
      statusOptions: {
        ...s.statusOptions,
        [columnId]: (s.statusOptions[columnId] || []).map((o) => (o.id === id ? option : o)),
      },
    }));
    return option;
  },

  // M3c, item 2: replacementOptionId é opcional — quando a etiqueta está em
  // uso o backend responde 400 (ver statusOption.controller.js) e quem chama
  // decide se reenvia com uma substituta. Propaga o erro (com err.status) em
  // vez de engolir, pra EditStatusOptionsModal distinguir "em uso" de outra falha.
  deleteStatusOption: async (id, columnId, replacementOptionId) => {
    const hasReplacement = replacementOptionId !== undefined && replacementOptionId !== null;
    await authFetch(`/api/v1/status-options/${id}`, {
      method: 'DELETE',
      ...(hasReplacement ? { body: JSON.stringify({ replacementOptionId }) } : {}),
    });
    set((s) => ({
      statusOptions: {
        ...s.statusOptions,
        [columnId]: (s.statusOptions[columnId] || []).filter((o) => o.id !== id),
      },
    }));
  },

  // ================= FAVORITOS =================
  fetchFavorites: async () => {
    set({ loadingFavorites: true });
    try {
      const folders = await authFetch('/api/v1/favorites/folders');
      set({ favoriteFolders: folders });
    } catch (err) {
      console.error('fetchFavorites failed:', err);
    } finally {
      set({ loadingFavorites: false });
    }
  },

  createFavoriteFolder: async (name) => {
    const folder = await authFetch('/api/v1/favorites/folders', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
    set((s) => ({ favoriteFolders: [...s.favoriteFolders, folder] }));
    return folder;
  },

  renameFavoriteFolder: async (id, name) => {
    const folder = await authFetch(`/api/v1/favorites/folders/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    });
    set((s) => ({
      favoriteFolders: s.favoriteFolders.map((f) => (f.id === id ? { ...f, ...folder } : f)),
    }));
  },

  deleteFavoriteFolder: async (id) => {
    await authFetch(`/api/v1/favorites/folders/${id}`, { method: 'DELETE' });
    set((s) => ({ favoriteFolders: s.favoriteFolders.filter((f) => f.id !== id) }));
  },

  addBoardToFolder: async (folderId, boardId) => {
    const folder = get().favoriteFolders.find((f) => f.id === folderId);
    if (folder?.boards?.some((b) => b.id === boardId)) return; // ja esta
    await authFetch(`/api/v1/favorites/folders/${folderId}/boards`, {
      method: 'POST',
      body: JSON.stringify({ board_id: boardId }),
    });
    const board = get().boards.find((b) => b.id === boardId);
    if (!board) return;
    set((s) => ({
      favoriteFolders: s.favoriteFolders.map((f) =>
        f.id === folderId
          ? { ...f, boards: [...(f.boards || []), { id: board.id, name: board.name, workspace_id: board.workspace_id }] }
          : f
      ),
    }));
  },

  removeBoardFromFolder: async (folderId, boardId) => {
    await authFetch(`/api/v1/favorites/folders/${folderId}/boards/${boardId}`, {
      method: 'DELETE',
    });
    set((s) => ({
      favoriteFolders: s.favoriteFolders.map((f) =>
        f.id === folderId ? { ...f, boards: (f.boards || []).filter((b) => b.id !== boardId) } : f
      ),
    }));
  },

  // ================= PASTAS DE WORKSPACE =================
  fetchWorkspaceFolders: async (workspaceId) => {
    const folders = await authFetch(`/api/v1/workspaces/${workspaceId}/folders`);
    set((s) => {
      const other = s.workspaceFolders.filter((f) => f.workspace_id !== workspaceId);
      return { workspaceFolders: [...other, ...folders] };
    });
    return folders;
  },

  createWorkspaceFolder: async (workspaceId, name) => {
    const folder = await authFetch(`/api/v1/workspaces/${workspaceId}/folders`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
    set((s) => ({ workspaceFolders: [...s.workspaceFolders, folder] }));
    return folder;
  },

  renameWorkspaceFolder: async (id, name) => {
    const folder = await authFetch(`/api/v1/workspace-folders/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    });
    set((s) => ({
      workspaceFolders: s.workspaceFolders.map((f) => (f.id === id ? { ...f, ...folder } : f)),
    }));
    return folder;
  },

  deleteWorkspaceFolder: async (id) => {
    await authFetch(`/api/v1/workspace-folders/${id}`, { method: 'DELETE' });
    set((s) => ({
      workspaceFolders: s.workspaceFolders.filter((f) => f.id !== id),
      // boards no folder viram soltos
      boards: s.boards.map((b) => (b.folder_id === id ? { ...b, folder_id: null } : b)),
    }));
  },

  moveBoardToWorkspaceFolder: async (boardId, folderId) => {
    const updated = await authFetch(`/api/v1/boards/${boardId}/folder`, {
      method: 'PUT',
      body: JSON.stringify({ folder_id: folderId }),
    });
    set((s) => ({
      boards: s.boards.map((b) => (b.id === boardId ? { ...b, folder_id: updated.folder_id } : b)),
    }));
    return updated;
  },

  // Move uma PASTA INTEIRA (com seus boards) para outro workspace.
  // Optimistic: muda workspace_id da pasta e de seus boards (boards mantêm folder_id).
  // Fallback: refetch de folders + boards de origem e destino.
  moveFolderToWorkspace: async (folderId, workspaceId) => {
    const sourceWsId = get().workspaceFolders.find((f) => f.id === folderId)?.workspace_id;
    // Optimistic
    set((s) => ({
      workspaceFolders: s.workspaceFolders.map((f) =>
        f.id === folderId ? { ...f, workspace_id: workspaceId } : f
      ),
      boards: s.boards.map((b) =>
        b.folder_id === folderId ? { ...b, workspace_id: workspaceId } : b
      ),
    }));
    try {
      const folder = await authFetch(`/api/v1/workspace-folders/${folderId}/workspace`, {
        method: 'PUT',
        body: JSON.stringify({ workspace_id: workspaceId }),
      });
      // Reconcilia a pasta com o que o backend retornou (position atualizado)
      set((s) => ({
        workspaceFolders: s.workspaceFolders.map((f) => (f.id === folderId ? { ...f, ...folder } : f)),
      }));
      return folder;
    } catch (err) {
      // Reverte refazendo o fetch dos dois workspaces
      if (sourceWsId) {
        await get().fetchWorkspaceFolders(sourceWsId);
        await get().fetchBoards(sourceWsId);
      }
      await get().fetchWorkspaceFolders(workspaceId);
      await get().fetchBoards(workspaceId);
      throw err;
    }
  },

  // Reordena boards dentro de um escopo (workspace + folder).
  // orderedIds: array com os IDs na ordem desejada.
  // folderId: null para root do workspace, ou ID da workspace_folder.
  reorderBoards: async ({ workspaceId, folderId, orderedIds }) => {
    // Optimistic update local: aplica a nova ordem imediatamente
    set((s) => {
      const posMap = new Map(orderedIds.map((id, i) => [id, (i + 1) * 1000]));
      const boards = s.boards.map((b) => {
        if (!posMap.has(b.id)) return b;
        return { ...b, position: posMap.get(b.id), folder_id: folderId };
      });
      return { boards };
    });
    try {
      await authFetch('/api/v1/boards/reorder', {
        method: 'POST',
        body: JSON.stringify({
          workspace_id: workspaceId,
          folder_id: folderId,
          order: orderedIds,
        }),
      });
    } catch (err) {
      // Se falhar, refetch para garantir consistência
      await get().fetchBoards(workspaceId);
      throw err;
    }
  },

  // ================= SELECAO DE BOARDS (SIDEBAR) =================
  toggleBoardSelection: (id) => set((s) => {
    const set_ = new Set(s.selectedBoardIds);
    if (set_.has(id)) set_.delete(id); else set_.add(id);
    return { selectedBoardIds: [...set_] };
  }),
  clearBoardSelection: () => set({ selectedBoardIds: [] }),

  setSidebarDropHint: (hint) => set({ sidebarDropHint: hint }),
  setSidebarDragKind: (kind) => set({ sidebarDragKind: kind }),

  // Reordena a lista UNICA da raiz (pastas + boards soltos) num espaco de position
  // compartilhado. items: [{ kind:'folder'|'board', id }] na ordem visual final.
  reorderRoot: async ({ workspaceId, items }) => {
    // Optimistic: aplica position = (i+1)*1000 a pastas e boards-raiz
    set((s) => {
      const posByKey = new Map(items.map((it, i) => [`${it.kind}-${it.id}`, (i + 1) * 1000]));
      const workspaceFolders = s.workspaceFolders.map((f) => {
        const p = posByKey.get(`folder-${f.id}`);
        return p != null ? { ...f, position: p } : f;
      });
      const boards = s.boards.map((b) => {
        const p = posByKey.get(`board-${b.id}`);
        return p != null ? { ...b, position: p } : b;
      });
      return { workspaceFolders, boards };
    });
    try {
      await authFetch(`/api/v1/workspaces/${workspaceId}/reorder-root`, {
        method: 'POST',
        body: JSON.stringify({ items: items.map((it) => ({ type: it.kind, id: it.id })) }),
      });
    } catch (err) {
      await get().fetchWorkspaceFolders(workspaceId);
      await get().fetchBoards(workspaceId);
      throw err;
    }
  },

  // ================= RECENTES =================
  loadRecents: (userId) => {
    if (!userId) return;
    set({ recentBoardIds: loadRecentsFromStorage(userId), recentUserId: userId });
  },

  pushRecent: (boardId) => {
    const { recentUserId, recentBoardIds } = get();
    if (!recentUserId || !boardId) return;
    const next = [boardId, ...recentBoardIds.filter((id) => id !== boardId)].slice(0, RECENT_LIMIT);
    saveRecentsToStorage(recentUserId, next);
    set({ recentBoardIds: next });
  },

  clearRecents: () => {
    const { recentUserId } = get();
    if (recentUserId) localStorage.removeItem(RECENT_KEY(recentUserId));
    set({ recentBoardIds: [] });
  },
}));

export default useBoardStore;
